import { useContext } from 'react';
import * as React from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';

// Components
import { Net } from './Net';
import { Sale } from './Sale';
import { Retail } from './Retail';
import { PricePaddleIcon } from './PricePaddleIcon';

// Helpers
import { textPriceValue, types, isValidTextPriceType } from '../helpers/priceTypeConstants';
import { TileContext } from '../helpers/TileContext';
import { FontSizeType } from '../types/StyleSizes';
import { useAuctionState } from 'dibs-auctions/exports/hooks';
import { retailLabel, startingBidLabel } from './messages';

import { Price_item$data as ItemType } from './__generated__/Price_item.graphql';

// Styles
import styles from './styles.scss';

type PriceProps = {
    currency: string;
    item: ItemType | null;
    hidePercentageOff?: boolean;
    priceBold?: boolean;
    isSbTile?: boolean;
    priceFontSize?: FontSizeType;
    isMobile?: boolean;
};

export const PriceComponent: React.FC<PriceProps> = ({
    currency,
    item,
    hidePercentageOff,
    priceBold,
    isSbTile,
    priceFontSize,
    isMobile,
}) => {
    const { imageSize = 'imageMedium', fontSize = 'fontSmall' } = useContext(TileContext);
    const { isPreviewAuction, hasAuctionEndedWithNoWinner } = useAuctionState({ item });

    const isAuctionEnabled = !!item?.isAuctionEnabled;
    const displayPrice = item?.displayPrice?.[0];
    // Price display for SB page type always returns a single price type
    const {
        textType,
        amountType,
        convertedAmountList,
        quantityDisplay,
        percentageOff,
        showPriceLabel,
        showPriceVariability,
    } = displayPrice || {};
    const percentageValue = hidePercentageOff ? null : percentageOff;

    const amountByCurrency = (convertedAmountList || []).find(
        convertedAmount => convertedAmount?.currency === currency
    );

    const price = Math.floor(amountByCurrency?.amount || 0) || 0;

    const sharedProps = {
        currency,
        price,
        quantityDisplay,
        showPriceVariability,
        isSbTile,
    };

    const isValidTextPrice = isValidTextPriceType(textType);

    const textTypePriceClass = classnames(
        styles.price,
        styles[priceFontSize || fontSize],
        {
            [styles.alert]:
                textType === types.SOLD ||
                textType === types.HOLD ||
                textType === types.UNAVAILABLE,
            [styles.isSbTile]: isSbTile,
        },
        styles[imageSize]
    );

    const priceClass = classnames(
        styles.price,
        styles[priceFontSize || fontSize],
        {
            [styles.isSbTile]: isSbTile,
            [styles.priceBold]: priceBold,
            [styles.priceLineThrough]: isValidTextPrice,
        },
        styles[imageSize]
    );

    if (isAuctionEnabled && hasAuctionEndedWithNoWinner) {
        return (
            <div className={priceClass} data-tn="price-auction-bidding-closed">
                <PricePaddleIcon />
                <FormattedMessage
                    id="dc.searchProductTile.biddingClosed"
                    defaultMessage="Bidding Closed"
                />
            </div>
        );
    }

    if (isValidTextPrice) {
        if (isAuctionEnabled) {
            const showPrice = price > 0 && textType !== types.UNAVAILABLE;
            return (
                <div data-tn={`price-auction-${textType}`} className={styles.textTypePriceWrapper}>
                    <div className={textTypePriceClass}>
                        <PricePaddleIcon />
                        {textPriceValue(textType)}
                    </div>
                    {showPrice && (
                        <div className={priceClass} data-tn="price-auction-retail">
                            &nbsp;|&nbsp;
                            <Retail {...sharedProps} />
                        </div>
                    )}
                </div>
            );
        }

        const showPrice = price > 0 && textType !== types.PUR;
        return (
            <div data-tn={`price-${textType}`} className={styles.textTypePriceWrapper}>
                <div className={textTypePriceClass}>{textPriceValue(textType)}</div>
                {showPrice && (
                    <div className={priceClass} data-tn="price-retail">
                        &nbsp;|&nbsp;
                        <Retail {...sharedProps} />
                    </div>
                )}
            </div>
        );
    }

    if (!price || !amountType) {
        return null;
    }

    if (amountType === types.SALE) {
        return (
            <div className={priceClass} data-tn="price-sale">
                <Sale {...sharedProps} percentageOff={percentageValue} />
            </div>
        );
    } else if (amountType === types.NET) {
        return (
            <div className={priceClass} data-tn="price-net">
                <Net {...sharedProps} percentageOff={percentageValue} />
            </div>
        );
    } else if (amountType === types.RETAIL) {
        if (isAuctionEnabled) {
            return (
                <div className={priceClass} data-tn="price-auction-retail">
                    <PricePaddleIcon />
                    <Retail
                        {...sharedProps}
                        label={isPreviewAuction && !isMobile ? startingBidLabel : ''}
                    />
                </div>
            );
        }
        return (
            <div className={priceClass} data-tn="price-retail">
                <Retail {...sharedProps} label={showPriceLabel ? retailLabel : ''} />
            </div>
        );
    }

    return null;
};

PriceComponent.defaultProps = {
    currency: 'USD',
    isSbTile: false,
};

export const Price = createFragmentContainer(PriceComponent, {
    item: graphql`
        fragment Price_item on Item
        @argumentDefinitions(
            isTrade: { type: "Boolean", defaultValue: false }
            pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
            showPrice: { type: "Boolean", defaultValue: true }
        ) {
            isAuctionEnabled
            displayPrice(page: $pageDisplayEnum, isTrade: $isTrade) @include(if: $showPrice) {
                convertedAmountList {
                    amount
                    currency
                }
                quantityDisplay
                percentageOff
                textType
                amountType
                showPriceLabel
                showPriceVariability
            }
            ...useAuctionState_item @arguments(page: $pageDisplayEnum)
        }
    `,
});
